import React, { useEffect, useMemo, useRef, useState } from 'react';
import QRCode from 'qrcode';
import { httpsCallable } from 'firebase/functions';
import { auth, db, functions } from '../firebaseConfig';
import { useOrgId } from '../utils/tenant';
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
} from 'firebase/firestore';

const orgCol = (orgId, sub) => collection(db, 'orgs', orgId, sub);

function getQueryOrgId() {
  try {
    const params = new URLSearchParams(window.location.search);
    return params.get('orgId') || params.get('org') || '';
  } catch {
    return '';
  }
}

function resolveOrgId({ orgIdProp, org, hookOrgId }) {
  const queryOrgId = getQueryOrgId();
  return (
    orgIdProp ||
    org?.id ||
    org?.orgId ||
    org?.value ||
    queryOrgId ||
    hookOrgId ||
    ''
  );
}


function normalizeBusinessMode(raw) {
  const value = String(raw || '').trim().toLowerCase().replace(/-/g, '_').replace(/\s+/g, '_');
  const map = {
    hospitality: 'hospitality_retail',
    retail: 'hospitality_retail',
    hospitality_retail: 'hospitality_retail',
    security: 'security_services',
    security_services: 'security_services',
    cleaning: 'cleaning_services',
    cleaning_services: 'cleaning_services',
    field: 'field_services',
    field_services: 'field_services',
  };
  return map[value] || 'hospitality_retail';
}

function normalizeAppVariant(raw, businessMode = '') {
  const value = String(raw || '').trim().toLowerCase().replace(/-/g, '_').replace(/\s+/g, '_');
  if (value === 'personal_device' || value === 'shared_device') return value;
  return isFieldMode(businessMode) ? 'personal_device' : 'shared_device';
}

function isFieldMode(mode) {
  return ['security_services', 'cleaning_services', 'field_services'].includes(normalizeBusinessMode(mode));
}

const NEO_APP_BASE = 'https://neo.matrixos.co.za';

function normalizeBase(raw) {
  try {
    const u = new URL(raw);
    if ((u.hostname === 'localhost' || u.hostname === '127.0.0.1') && u.port === '3000') {
      u.port = '3001';
      return u.toString();
    }

    // Retire the legacy InvoiceMate Neo hostname even if an old Netlify
    // environment variable is still present in a production deployment.
    if (u.hostname === 'neo.invoicemate.co.za') {
      return NEO_APP_BASE;
    }

    return u.toString();
  } catch {
    return raw;
  }
}

function detectDefaultBase() {
  const env = process.env.REACT_APP_NEO_APP_BASE || process.env.REACT_APP_CASHUP_BASE;
  if (env) return normalizeBase(env);

  const { protocol, hostname, port } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    if (port === '3000') return `${protocol}//${hostname}:3001`;
    return `${protocol}//${hostname}${port ? `:${port}` : ''}`;
  }

  // Download links are generated from the Netlify-hosted admin panel but
  // must always open the standalone Firebase-hosted Neo device app.
  return NEO_APP_BASE;
}

function useCountdown(expMs) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!expMs) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [expMs]);

  const remaining = expMs ? Math.max(0, expMs - now) : 0;
  const expired = expMs ? remaining <= 0 : false;
  const label = expMs
    ? `${String(Math.floor(remaining / 60000)).padStart(2, '0')}:${String(
        Math.floor((remaining % 60000) / 1000)
      ).padStart(2, '0')}`
    : '';

  return { expired, label };
}

function useCopy() {
  const [copiedKey, setCopiedKey] = useState('');
  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text || '');
      setCopiedKey(key);
      setTimeout(() => setCopiedKey(''), 1200);
    } catch {
      // noop
    }
  };
  return { copiedKey, copy };
}

function pickFirst(...values) {
  for (const value of values) {
    if (value === undefined || value === null || value === '') continue;
    return value;
  }
  return null;
}

function toDateValue(ts) {
  try {
    if (!ts) return null;
    if (typeof ts?.toDate === 'function') {
      const d = ts.toDate();
      return Number.isNaN(d?.getTime?.()) ? null : d;
    }
    if (typeof ts === 'number') {
      const ms = ts < 1000000000000 ? ts * 1000 : ts;
      const d = new Date(ms);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    if (typeof ts === 'string') {
      const parsedNumber = Number(ts);
      if (Number.isFinite(parsedNumber) && String(parsedNumber) === ts.trim()) {
        const ms = parsedNumber < 1000000000000 ? parsedNumber * 1000 : parsedNumber;
        const d = new Date(ms);
        return Number.isNaN(d.getTime()) ? null : d;
      }
      const d = new Date(ts);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    if (typeof ts === 'object') {
      if (typeof ts.seconds === 'number') {
        const d = new Date(ts.seconds * 1000 + Math.floor((ts.nanoseconds || 0) / 1000000));
        return Number.isNaN(d.getTime()) ? null : d;
      }
      if (typeof ts._seconds === 'number') {
        const d = new Date(ts._seconds * 1000 + Math.floor((ts._nanoseconds || 0) / 1000000));
        return Number.isNaN(d.getTime()) ? null : d;
      }
    }
    const d = new Date(ts);
    return Number.isNaN(d.getTime()) ? null : d;
  } catch {
    return null;
  }
}

function tsMs(ts) {
  return toDateValue(ts)?.getTime() || 0;
}

function fmtTs(ts) {
  const d = toDateValue(ts);
  return d ? d.toLocaleString() : '—';
}

function normalizeDevice(raw = {}) {
  const uid = String(
    pickFirst(
      raw.uid,
      raw.deviceUid,
      raw.deviceId,
      raw.bindingUid,
      raw.installationUid,
      raw.id
    ) || ''
  );
  const linkedAt = pickFirst(
    raw.linkedAt,
    raw.linkedOn,
    raw.linked_at,
    raw.linkedDate,
    raw.linkedTs,
    raw.linkedTimestamp,
    raw.createdAt,
    raw.created_at,
    raw.boundAt,
    raw.bound_at,
    raw.bindingCreatedAt,
    raw.installAt,
    raw.installAtMs,
    raw.installedAt,
    raw.firstSeenAt,
    raw.first_seen_at,
    raw.meta?.linkedAt,
    raw.meta?.createdAt,
    raw.binding?.linkedAt,
    raw.binding?.createdAt,
    raw.updatedAt
  );
  const lastUsedAt = pickFirst(
    raw.lastUsedAt,
    raw.lastSeenAt,
    raw.lastSeen,
    raw.last_seen,
    raw.lastActiveAt,
    raw.lastActive,
    raw.lastHeartbeatAt,
    raw.heartbeatAt,
    raw.seenAt,
    raw.usedAt,
    raw.lastUsed,
    raw.lastOpenedAt,
    raw.last_opened_at,
    raw.meta?.lastUsedAt,
    raw.meta?.lastSeenAt,
    raw.binding?.lastUsedAt,
    raw.binding?.lastSeenAt,
    raw.updatedAt,
    raw.updated_at,
    raw.last_used_at
  );
  const status = String(
    pickFirst(raw.status, raw.linkStatus, raw.deviceStatus, raw.bindingStatus, raw.state) || 'linked'
  ).toLowerCase();

  return {
    ...raw,
    uid,
    storeId: String(pickFirst(raw.storeId, raw.store, raw.branchId, raw.locationId) || ''),
    storeName: String(pickFirst(raw.storeName, raw.storeLabel, raw.store, raw.branchName, raw.locationName) || ''),
    userAgent: String(pickFirst(raw.userAgent, raw.ua, raw.browser, raw.agent) || ''),
    deviceLabel: String(pickFirst(raw.deviceLabel, raw.deviceName, raw.label, raw.name, raw.model) || ''),
    installPin: String(pickFirst(raw.installPin, raw.pin, raw.linkPin) || ''),
    linkedAt,
    lastUsedAt,
    lastSeenAt: lastUsedAt,
    status,
    lastReason: String(pickFirst(raw.lastReason, raw.killReason, raw.reason) || ''),
  };
}



const DEVICE_COLLECTION_CANDIDATES = [
  'deviceBindings',
  'device_bindings',
  'linkedDevices',
  'linked_devices',
  'devices',
  'trusted_devices',
  'trustedDevices',
  'installPins',
  'install_pins',
];

function extractDeviceRowsFromSnapshot(snapshot, collectionName) {
  return snapshot.docs
    .map((snap) => {
      const raw = { id: snap.id, ...(snap.data() || {}) };
      const normalized = normalizeDevice(raw);
      const hasDeviceIdentity = Boolean(normalized.uid);
      const looksLinked = Boolean(
        pickFirst(
          raw.isLinked,
          raw.linked,
          raw.bound,
          raw.deviceUid,
          raw.uid,
          raw.bindingUid,
          raw.installationUid,
          raw.lastUsedAt,
          raw.lastSeenAt,
          raw.lastHeartbeatAt
        )
      );
      const isInstallPinUsed =
        collectionName === 'installPins' &&
        (raw.used === true || raw.isUsed === true || raw.redeemed === true);

      if (!hasDeviceIdentity && !looksLinked && !isInstallPinUsed) return null;

      const status = isInstallPinUsed ? 'linked' : String(normalized.status || '').toLowerCase();
      if (status === 'expired' || status === 'unlinked' || status === 'revoked') return null;

      return {
        ...normalized,
        uid: normalized.uid || String(raw.deviceUid || raw.uid || raw.bindingUid || snap.id),
        status: status || 'linked',
        _sourceCollection: collectionName,
      };
    })
    .filter(Boolean);
}


async function loadDevicesDirect(orgId) {
  const storeMap = new Map();
  try {
    const storesSnap = await getDocs(collection(db, 'orgs', orgId, 'cashup_stores'));
    storesSnap.forEach((snap) => {
      const data = snap.data() || {};
      storeMap.set(snap.id, String(data.name || data.storeName || data.label || ''));
    });
  } catch (e) {
    console.warn('[DownloadTab] failed to load store map for direct device read', e);
  }

  const directSnap = await getDocs(collection(db, 'orgs', orgId, 'deviceBindings'));
  return directSnap.docs
    .map((snap) => {
      const raw = { id: snap.id, ...(snap.data() || {}) };
      const item = normalizeDevice(raw);
      if (!item.uid) return null;
      const status = String(item.status || '').toLowerCase();
      if (status === 'expired' || status === 'unlinked' || status === 'revoked') return null;
      return {
        ...item,
        status: status || 'linked',
        storeName: item.storeName || storeMap.get(item.storeId) || '',
        _sourceCollection: 'deviceBindings',
      };
    })
    .filter(Boolean);
}

async function loadDevicesFromFirestoreFallback(orgId) {
  const settled = await Promise.allSettled(
    DEVICE_COLLECTION_CANDIDATES.map(async (collectionName) => {
      const snap = await getDocs(collection(db, 'orgs', orgId, collectionName));
      return extractDeviceRowsFromSnapshot(snap, collectionName);
    })
  );

  const merged = [];
  const seen = new Set();

  settled.forEach((result) => {
    if (result.status !== 'fulfilled' || !Array.isArray(result.value)) return;
    result.value.forEach((row) => {
      const key = String(row.uid || '').trim();
      if (!key || seen.has(key)) return;
      seen.add(key);
      merged.push(row);
    });
  });

  return merged;
}

function friendlyError(e) {
  const msg = String(e?.message || e?.details || 'Something went wrong');
  if (/wrong organization/i.test(msg)) {
    return 'Selected organization does not match the current page context. Re-select the org, then try again.';
  }
  if (/permission|forbidden|unauthenticated/i.test(msg)) {
    return 'You do not have permission for this organization. Refresh your session, then try again.';
  }
  if (/internal/i.test(msg)) {
    return 'The callable function returned an internal error. Refresh the page once, then try again.';
  }
  return msg;
}


function getLocalArchiveKey(type, orgId) {
  return `superArchive:${type}:${orgId || 'global'}`;
}

function readLocalArchive(type, orgId) {
  try {
    const raw = window.localStorage.getItem(getLocalArchiveKey(type, orgId));
    const rows = raw ? JSON.parse(raw) : [];
    return Array.isArray(rows) ? rows : [];
  } catch {
    return [];
  }
}

function pushLocalArchive(type, orgId, row) {
  try {
    const next = [row, ...readLocalArchive(type, orgId)].slice(0, 300);
    window.localStorage.setItem(getLocalArchiveKey(type, orgId), JSON.stringify(next));
    if (orgId) {
      const globalNext = [row, ...readLocalArchive(type, '')].slice(0, 600);
      window.localStorage.setItem(getLocalArchiveKey(type, ''), JSON.stringify(globalNext));
    }
  } catch {
    // noop
  }
}

function statusBadgeStyle(status) {
  const norm = String(status || 'linked').toLowerCase();
  if (norm === 'killed') {
    return {
      color: '#fecaca',
      background: 'rgba(127,29,29,0.25)',
      border: '1px solid rgba(248,113,113,0.25)',
    };
  }
  if (norm === 'linked') {
    return {
      color: '#bbf7d0',
      background: 'rgba(6,95,70,0.25)',
      border: '1px solid rgba(52,211,153,0.22)',
    };
  }
  return {
    color: '#e5e7eb',
    background: 'rgba(30,41,59,0.6)',
    border: '1px solid rgba(148,163,184,0.18)',
  };
}

async function writeActionArchive({ orgId, payload }) {
  const mirrorRow = {
    id: `${payload?.type || 'device_event'}:${payload?.deviceUid || 'unknown'}:${Date.now()}`,
    ...payload,
    createdAt: new Date().toISOString(),
    source: 'local_mirror',
  };
  pushLocalArchive('device_action_archive', orgId, mirrorRow);
  try {
    await addDoc(collection(db, 'orgs', orgId, 'device_action_archive'), {
      ...payload,
      createdAt: serverTimestamp(),
    });
    return true;
  } catch (e) {
    console.warn('[DownloadTab] archive log write skipped', e);
    return false;
  }
}

async function writeKilledDeviceArchive({ orgId, item, payload }) {
  const mirrorRow = {
    id: item.uid,
    orgId,
    orgName: payload.orgName || '',
    storeId: item.storeId || '',
    storeName: item.storeName || '',
    deviceUid: item.uid,
    deviceName: payload.deviceName || item.alias || item.deviceLabel || item.userAgent || item.storeName || item.storeId || '',
    reason: payload.reason || '',
    impact: payload.impact || '',
    type: payload.type || 'kill_device',
    statusBefore: payload.statusBefore || item.status || 'linked',
    actorUid: payload.actorUid || '',
    actorEmail: payload.actorEmail || '',
    linkedAt: item.linkedAt || null,
    lastUsedAt: item.lastUsedAt || item.lastSeenAt || null,
    scheduledExpiryAt: item.scheduledExpiryAt || null,
    archivedAt: new Date().toISOString(),
    killedAt: new Date().toISOString(),
    active: false,
    source: 'local_mirror',
  };
  pushLocalArchive('killed_devices', orgId, mirrorRow);
  try {
    await setDoc(
      doc(db, 'orgs', orgId, 'killed_devices', item.uid),
      {
        orgId,
        orgName: payload.orgName || '',
        storeId: item.storeId || '',
        storeName: item.storeName || '',
        deviceUid: item.uid,
        deviceName: payload.deviceName || item.alias || item.deviceLabel || item.userAgent || item.storeName || item.storeId || '',
        reason: payload.reason || '',
        impact: payload.impact || '',
        type: payload.type || 'kill_device',
        statusBefore: payload.statusBefore || item.status || 'linked',
        actorUid: payload.actorUid || '',
        actorEmail: payload.actorEmail || '',
        linkedAt: item.linkedAt || null,
        lastUsedAt: item.lastUsedAt || item.lastSeenAt || null,
        scheduledExpiryAt: item.scheduledExpiryAt || null,
        archivedAt: serverTimestamp(),
        killedAt: serverTimestamp(),
        active: false,
      },
      { merge: true }
    );
    return true;
  } catch (e) {
    console.warn('[DownloadTab] killed device archive write skipped', e);
    return false;
  }
}


async function mirrorKilledStateToFirestore({ orgId, item, reason = '', impact = '', actorEmail = '', actorUid = '' }) {
  if (!orgId || !item?.uid) return;

  const writes = [
    setDoc(
      doc(db, 'orgs', orgId, 'deviceBindings', item.uid),
      {
        status: 'killed',
        shouldUnlink: true,
        killedAt: serverTimestamp(),
        lastReason: reason || 'Killed by super admin',
        lastImpact: impact || '',
        actorUid: actorUid || '',
        actorEmail: actorEmail || '',
      },
      { merge: true }
    ),
  ];

  if (item.storeId) {
    writes.push(
      setDoc(
        doc(db, 'orgs', orgId, 'cashup_stores', item.storeId, 'devices', item.uid),
        {
          status: 'killed',
          shouldUnlink: true,
          killedAt: serverTimestamp(),
          lastReason: reason || 'Killed by super admin',
          lastImpact: impact || '',
          actorUid: actorUid || '',
          actorEmail: actorEmail || '',
        },
        { merge: true }
      )
    );
  }

  await Promise.allSettled(writes);
}

async function mirrorKillAllStateToFirestore({ orgId, items = [], reason = '', impact = '', actorEmail = '', actorUid = '' }) {
  if (!orgId || !Array.isArray(items) || !items.length) return;
  await Promise.allSettled(
    items.map((item) =>
      mirrorKilledStateToFirestore({
        orgId,
        item,
        reason,
        impact,
        actorEmail,
        actorUid,
      })
    )
  );
}

function KillConfirmModal({ open, item, busy, onClose, onConfirm }) {
  const [step, setStep] = useState(1);
  const [reason, setReason] = useState('');
  const [impact, setImpact] = useState('');

  useEffect(() => {
    if (!open) {
      setStep(1);
      setReason('');
      setImpact('');
      return;
    }
    setStep(1);
    setReason('');
    setImpact('');
  }, [open, item?.uid]);

  if (!open || !item) return null;

  const isBulk = !!item?.isBulk;
  const canContinue = step === 1 ? true : reason.trim().length >= 3;
  const title = step === 1 ? (isBulk ? 'Kill all linked devices?' : 'Kill linked device?') : (isBulk ? 'Reason for bulk kill' : 'Reason for kill');
  const subtitle =
    step === 1
      ? (isBulk
          ? 'This will unlink every active Neo device in the selected organization and force a relink.'
          : 'This will unlink the selected Neo device and force a relink.')
      : 'Add context so this action is traceable in the archive.';

  return (
    <div className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm px-4" style={{ display: 'grid', placeItems: 'center' }}>
      <div style={{ width: '100%', maxWidth: 560, background: '#0f172a', border: '1px solid #1f2937', borderRadius: 18, boxShadow: '0 30px 90px rgba(0,0,0,0.45)' }}>
        <div style={{ padding: '18px 20px', borderBottom: '1px solid #1f2937' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center' }}>
            <div>
              <div style={{ color: '#e5e7eb', fontSize: 20, fontWeight: 700 }}>{title}</div>
              <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 4 }}>{subtitle}</div>
            </div>
            <div style={{ color: '#38bdf8', fontSize: 12, fontWeight: 700 }}>STEP {step} / 2</div>
          </div>
        </div>

        <div style={{ padding: 20 }}>
          <div style={{ padding: 14, borderRadius: 14, background: '#0b1220', border: '1px solid #1e293b', marginBottom: 16 }}>
            <div style={{ color: '#e5e7eb', fontWeight: 700 }}>
              {isBulk ? (item.orgName || 'Selected organization') : (item.alias || item.storeName || item.storeId || 'Unknown device')}
            </div>
            <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 4 }}>
              {isBulk
                ? `This action targets all currently linked devices${item.count ? ` (${item.count})` : ''}.`
                : (item.deviceLabel || item.userAgent || 'Unknown device')}
            </div>
            <div style={{ color: '#64748b', fontSize: 12, marginTop: 8 }}>
              {isBulk ? `Scope: organization-wide` : `UID: ${item.uid}`}
            </div>
          </div>

          {step === 1 ? (
            <div style={{ display: 'grid', gap: 12 }}>
              <div style={{ padding: 14, borderRadius: 14, background: 'rgba(127,29,29,0.18)', border: '1px solid rgba(248,113,113,0.2)', color: '#fecaca', fontSize: 14 }}>
                The device will immediately lose access and the user will need to relink the Neo app.
              </div>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ color: '#94a3b8', fontSize: 13 }}>Impact note (optional)</span>
                <input
                  value={impact}
                  onChange={(e) => setImpact(e.target.value)}
                  placeholder="Example: Lost tablet, wrong branch, shared phone"
                  style={{ width: '100%', padding: '12px 13px', background: '#020617', color: '#e5e7eb', border: '1px solid #334155', borderRadius: 10 }}
                />
              </label>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: 12 }}>
              <label style={{ display: 'grid', gap: 6 }}>
                <span style={{ color: '#94a3b8', fontSize: 13 }}>Reason for kill *</span>
                <textarea
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  rows={4}
                  placeholder="Why is this device being killed?"
                  style={{ width: '100%', padding: '12px 13px', background: '#020617', color: '#e5e7eb', border: '1px solid #334155', borderRadius: 10, resize: 'vertical' }}
                />
              </label>
              <div style={{ color: '#64748b', fontSize: 12 }}>
                This reason will be stored in the archive log for later review.
              </div>
            </div>
          )}
        </div>

        <div style={{ padding: '16px 20px', borderTop: '1px solid #1f2937', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} disabled={busy} style={{ padding: '11px 14px', borderRadius: 10, border: '1px solid #334155', background: '#111827', color: '#e5e7eb', opacity: busy ? 0.65 : 1 }}>
            Cancel
          </button>
          {step === 2 ? (
            <button onClick={() => setStep(1)} disabled={busy} style={{ padding: '11px 14px', borderRadius: 10, border: '1px solid #334155', background: '#0f172a', color: '#cbd5e1', opacity: busy ? 0.65 : 1 }}>
              Back
            </button>
          ) : null}
          {step === 1 ? (
            <button onClick={() => setStep(2)} disabled={busy || !canContinue} style={{ padding: '11px 14px', borderRadius: 10, border: '1px solid #155e75', background: '#0891b2', color: '#fff', opacity: busy ? 0.65 : 1 }}>
              Continue
            </button>
          ) : (
            <button onClick={() => onConfirm({ reason: reason.trim(), impact: impact.trim() })} disabled={busy || !canContinue} style={{ padding: '11px 14px', borderRadius: 10, border: '1px solid #7f1d1d', background: '#991b1b', color: '#fff', opacity: busy || !canContinue ? 0.65 : 1 }}>
              {busy ? 'Sending…' : 'Confirm kill'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function hoursToLabel(hours) {
  const n = Number(hours || 0);
  if (!n) return '';
  return `${n} hr${n === 1 ? '' : 's'}`;
}

function ExpireSessionModal({ open, item, busy, onClose, onSchedule }) {
  const presets = [6, 12, 24, 48, 72];
  const [selected, setSelected] = useState(24);
  const [custom, setCustom] = useState('');
  const [reason, setReason] = useState('Session expiry scheduled by super admin');

  useEffect(() => {
    if (!open) {
      setSelected(24);
      setCustom('');
      setReason('Session expiry scheduled by super admin');
      return;
    }
    setSelected(24);
    setCustom('');
    setReason('Session expiry scheduled by super admin');
  }, [open, item?.uid]);

  if (!open || !item) return null;

  const hours = selected === 'custom' ? Number(custom || 0) : Number(selected || 0);
  const validHours = Number.isFinite(hours) && hours > 0;
  const target = validHours ? new Date(Date.now() + hours * 60 * 60 * 1000) : null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm px-4" style={{ display: 'grid', placeItems: 'center' }}>
      <div style={{ width: '100%', maxWidth: 620, background: '#0f172a', border: '1px solid #1f2937', borderRadius: 18, boxShadow: '0 30px 90px rgba(0,0,0,0.45)' }}>
        <div style={{ padding: '18px 20px', borderBottom: '1px solid #1f2937' }}>
          <div style={{ color: '#e5e7eb', fontSize: 20, fontWeight: 700 }}>Schedule session expiry</div>
          <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 4 }}>Pick when this device session should expire. When the timer hits, the kill switch should fire for that device.</div>
        </div>

        <div style={{ padding: 20, display: 'grid', gap: 16 }}>
          <div style={{ padding: 14, borderRadius: 14, background: '#0b1220', border: '1px solid #1e293b' }}>
            <div style={{ color: '#e5e7eb', fontWeight: 700 }}>{item.alias || item.storeName || item.storeId || 'Unknown device'}</div>
            <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 4 }}>{item.deviceLabel || item.userAgent || 'Unknown device'}</div>
            <div style={{ color: '#64748b', fontSize: 12, marginTop: 8 }}>UID: {item.uid}</div>
          </div>

          <div>
            <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 10 }}>Expire after</div>
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
              {presets.map((h) => (
                <button
                  key={h}
                  onClick={() => setSelected(h)}
                  type="button"
                  style={{
                    padding: '10px 14px',
                    borderRadius: 10,
                    border: selected === h ? '1px solid #155e75' : '1px solid #334155',
                    background: selected === h ? 'rgba(8,145,178,0.2)' : '#020617',
                    color: '#e5e7eb',
                  }}
                >
                  {hoursToLabel(h)}
                </button>
              ))}
              <button
                onClick={() => setSelected('custom')}
                type="button"
                style={{
                  padding: '10px 14px',
                  borderRadius: 10,
                  border: selected === 'custom' ? '1px solid #155e75' : '1px solid #334155',
                  background: selected === 'custom' ? 'rgba(8,145,178,0.2)' : '#020617',
                  color: '#e5e7eb',
                }}
              >
                Custom
              </button>
            </div>
          </div>

          {selected === 'custom' ? (
            <label style={{ display: 'grid', gap: 6 }}>
              <span style={{ color: '#94a3b8', fontSize: 13 }}>Custom hours</span>
              <input
                type="number"
                min={1}
                step={1}
                value={custom}
                onChange={(e) => setCustom(e.target.value)}
                placeholder="Enter hours"
                style={{ width: '100%', padding: '12px 13px', background: '#020617', color: '#e5e7eb', border: '1px solid #334155', borderRadius: 10 }}
              />
            </label>
          ) : null}

          <label style={{ display: 'grid', gap: 6 }}>
            <span style={{ color: '#94a3b8', fontSize: 13 }}>Reason</span>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              placeholder="Why is this session expiring?"
              style={{ width: '100%', padding: '12px 13px', background: '#020617', color: '#e5e7eb', border: '1px solid #334155', borderRadius: 10, resize: 'vertical' }}
            />
          </label>

          <div style={{ padding: 14, borderRadius: 14, background: 'rgba(8,145,178,0.08)', border: '1px solid rgba(56,189,248,0.16)' }}>
            <div style={{ color: '#94a3b8', fontSize: 13 }}>Scheduled kill time</div>
            <div style={{ color: '#e5e7eb', fontSize: 15, fontWeight: 700, marginTop: 4 }}>{target ? target.toLocaleString() : 'Select a valid duration'}</div>
          </div>
        </div>

        <div style={{ padding: '16px 20px', borderTop: '1px solid #1f2937', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} disabled={busy} style={{ padding: '11px 14px', borderRadius: 10, border: '1px solid #334155', background: '#111827', color: '#e5e7eb', opacity: busy ? 0.65 : 1 }}>
            Cancel
          </button>
          <button
            onClick={() => onSchedule({ hours, reason: reason.trim(), expiresAtMs: target ? target.getTime() : 0 })}
            disabled={busy || !validHours}
            style={{ padding: '11px 14px', borderRadius: 10, border: '1px solid #155e75', background: '#0891b2', color: '#fff', opacity: busy || !validHours ? 0.65 : 1 }}
          >
            {busy ? 'Scheduling…' : 'Schedule expiry'}
          </button>
        </div>
      </div>
    </div>
  );
}


function NoticeModal({ open, title = 'Notice', message = '', onClose }) {
  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm px-4" style={{ display: 'grid', placeItems: 'center' }}>
      <div style={{ width: '100%', maxWidth: 520, background: '#0f172a', border: '1px solid #1f2937', borderRadius: 18, boxShadow: '0 30px 90px rgba(0,0,0,0.45)' }}>
        <div style={{ padding: '18px 20px', borderBottom: '1px solid #1f2937' }}>
          <div style={{ color: '#e5e7eb', fontSize: 20, fontWeight: 700 }}>{title}</div>
        </div>
        <div style={{ padding: 20 }}>
          <div style={{ color: '#cbd5e1', fontSize: 14, lineHeight: 1.6 }}>{message}</div>
        </div>
        <div style={{ padding: '16px 20px', borderTop: '1px solid #1f2937', display: 'flex', justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{ padding: '11px 14px', borderRadius: 10, border: '1px solid #155e75', background: '#0891b2', color: '#fff' }}
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
}

function RenameDeviceModal({ open, item, busy, onClose, onSave }) {
  const [value, setValue] = useState('');

  useEffect(() => {
    setValue(item?.alias || item?.deviceLabel || item?.storeName || '');
  }, [item, open]);

  if (!open || !item) return null;

  return (
    <div className="fixed inset-0 z-[9999] bg-black/70 backdrop-blur-sm px-4" style={{ display: 'grid', placeItems: 'center' }}>
      <div style={{ width: '100%', maxWidth: 520, background: '#0f172a', border: '1px solid #1f2937', borderRadius: 18 }}>
        <div style={{ padding: '18px 20px', borderBottom: '1px solid #1f2937' }}>
          <div style={{ color: '#e5e7eb', fontSize: 20, fontWeight: 700 }}>Rename device</div>
          <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 4 }}>Give this device a clear label so you do not kill the wrong one.</div>
        </div>
        <div style={{ padding: 20, display: 'grid', gap: 12 }}>
          <div style={{ color: '#94a3b8', fontSize: 13 }}>Current UID: {item.uid}</div>
          <input
            autoFocus
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="Example: District Six Front Counter Tablet"
            style={{ width: '100%', padding: '12px 13px', background: '#020617', color: '#e5e7eb', border: '1px solid #334155', borderRadius: 10 }}
          />
          <div style={{ color: '#64748b', fontSize: 12 }}>Saved to device aliases. If Firestore write is blocked, it still falls back to this browser.</div>
        </div>
        <div style={{ padding: '16px 20px', borderTop: '1px solid #1f2937', display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          <button onClick={onClose} disabled={busy} style={{ padding: '11px 14px', borderRadius: 10, border: '1px solid #334155', background: '#111827', color: '#e5e7eb', opacity: busy ? 0.65 : 1 }}>Cancel</button>
          <button onClick={() => onSave(value.trim())} disabled={busy || !value.trim()} style={{ padding: '11px 14px', borderRadius: 10, border: '1px solid #155e75', background: '#0891b2', color: '#fff', opacity: busy || !value.trim() ? 0.65 : 1 }}>
            {busy ? 'Saving…' : 'Save name'}
          </button>
        </div>
      </div>
    </div>
  );
}

function DeviceRow({ item, onKill, onExpire, onRename, busy }) {
  const ua = item.deviceLabel || item.userAgent || 'Unknown device';
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(300px,1.35fr) minmax(160px,0.8fr) minmax(140px,0.8fr) minmax(140px,0.9fr) auto',
        gap: 12,
        padding: '14px 16px',
        borderTop: '1px solid #1f2937',
        alignItems: 'center',
      }}
    >
      <div>
        <div style={{ color: '#e5e7eb', fontWeight: 700, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <span>{item.alias || item.storeName || item.storeId || 'Unknown device'}</span>
          {item.alias ? <span style={{ fontSize: 11, color: '#38bdf8', padding: '2px 8px', borderRadius: 999, border: '1px solid rgba(56,189,248,0.28)', background: 'rgba(8,145,178,0.1)' }}>Custom name</span> : null}
        </div>
        <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 4 }}>{ua}</div>
        <div style={{ color: '#64748b', fontSize: 11, marginTop: 4 }}>UID: {item.uid}</div>
        {item.lastReason ? <div style={{ color: '#fca5a5', fontSize: 11, marginTop: 4 }}>Last reason: {item.lastReason}</div> : null}
        {item.scheduledExpiryAt ? <div style={{ color: '#67e8f9', fontSize: 11, marginTop: 4 }}>Scheduled expiry: {fmtTs(item.scheduledExpiryAt)}</div> : null}
      </div>

      <div>
        <div style={{ display: 'inline-flex', padding: '5px 10px', borderRadius: 999, fontSize: 12, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, ...statusBadgeStyle(item.status) }}>
          {item.status || 'linked'}
        </div>
        <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 8 }}>PIN: {item.installPin || '—'}</div>
      </div>

      <div>
        <div style={{ color: '#94a3b8', fontSize: 12 }}>Linked on</div>
        <div style={{ color: '#e5e7eb', fontSize: 13, marginTop: 4 }}>{fmtTs(item.linkedAt)}</div>
      </div>

      <div>
        <div style={{ color: '#94a3b8', fontSize: 12 }}>Last used</div>
        <div style={{ color: '#e5e7eb', fontSize: 13, marginTop: 4 }}>{fmtTs(item.lastUsedAt || item.lastSeenAt)}</div>
      </div>

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
        <button
          onClick={() => onRename(item)}
          disabled={busy}
          style={{
            padding: '10px 12px',
            borderRadius: 8,
            border: '1px solid #1d4ed8',
            background: '#0f172a',
            color: '#bfdbfe',
            cursor: busy ? 'not-allowed' : 'pointer',
            opacity: busy ? 0.6 : 1,
          }}
        >
          Rename
        </button>
        <button
          onClick={() => onExpire(item)}
          disabled={busy || item.status === 'killed'}
          style={{
            padding: '10px 12px',
            borderRadius: 8,
            border: '1px solid #92400e',
            background: item.status === 'killed' ? '#1f2937' : '#9a3412',
            color: '#fff',
            cursor: busy || item.status === 'killed' ? 'not-allowed' : 'pointer',
            opacity: busy || item.status === 'killed' ? 0.6 : 1,
          }}
        >
          Schedule expiry
        </button>
        <button
          onClick={() => onKill(item)}
          disabled={busy || item.status === 'killed'}
          style={{
            padding: '10px 12px',
            borderRadius: 8,
            border: '1px solid #7f1d1d',
            background: item.status === 'killed' ? '#1f2937' : '#7f1d1d',
            color: '#fff',
            cursor: busy || item.status === 'killed' ? 'not-allowed' : 'pointer',
            opacity: busy || item.status === 'killed' ? 0.6 : 1,
          }}
        >
          {item.status === 'killed' ? 'Killed' : 'Kill switch'}
        </button>
      </div>
    </div>
  );
}

export default function DownloadTab({ orgId: orgIdProp, org }) {
  const hookOrgId = useOrgId();
  const orgId = useMemo(
    () => String(resolveOrgId({ orgIdProp, org, hookOrgId }) || '').trim(),
    [orgIdProp, org, hookOrgId]
  );

  const { copiedKey, copy } = useCopy();
  useEffect(() => {
    try {
      if (!orgId) return;
      const params = new URLSearchParams(window.location.search);
      const current = params.get('orgId') || params.get('org') || '';
      if (String(current).trim() === String(orgId).trim()) return;
      params.set('orgId', orgId);
      params.delete('org');
      const next = `${window.location.pathname}?${params.toString()}`;
      window.history.replaceState({}, '', next);
    } catch {
      // noop
    }
  }, [orgId]);


  const showNotice = (title, message) => {
    setNotice({ open: true, title, message });
  };

  const ensureFreshAuth = async () => {
    const user = auth.currentUser;
    if (!user) throw new Error('You are signed out. Please sign in again.');
    try {
      await user.getIdToken(true);
    } catch (e) {
      console.warn('[DownloadTab] token refresh failed', e);
    }
    return user;
  };

  const [orgName, setOrgName] = useState('');
  const [businessMode, setBusinessMode] = useState('hospitality_retail');
  const [appVariant, setAppVariant] = useState('shared_device');
  const [advanced, setAdvanced] = useState(false);
  const [base] = useState(detectDefaultBase());
  const [stores, setStores] = useState([]);
  const [storeId, setStoreId] = useState('');
  const [ttlMin, setTtlMin] = useState(15);
  const [genLoading, setGenLoading] = useState(false);
  const [pin, setPin] = useState('');
  const [token, setToken] = useState('');
  const [expAtMs, setExpAtMs] = useState(null);
  const [devices, setDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [deviceErr, setDeviceErr] = useState('');
  const [busyKill, setBusyKill] = useState(false);
  const [busyRename, setBusyRename] = useState(false);
  const [deviceMsg, setDeviceMsg] = useState('');
  const [aliases, setAliases] = useState({});
  const [killModalItem, setKillModalItem] = useState(null);
  const [killAllModalOpen, setKillAllModalOpen] = useState(false);
  const [renameModalItem, setRenameModalItem] = useState(null);
  const [expireModalItem, setExpireModalItem] = useState(null);
  const [scheduledExpiries, setScheduledExpiries] = useState({});
  const [notice, setNotice] = useState({ open: false, title: '', message: '' });

  const perStoreCanvasRef = useRef(null);
  const orgCanvasRef = useRef(null);
  const { expired, label } = useCountdown(expAtMs);

  useEffect(() => {
    setStores([]);
    setStoreId('');
    setPin('');
    setToken('');
    setExpAtMs(null);
    setDevices([]);
    setDeviceErr('');
    setDeviceMsg('');
    setAliases({});
    setKillModalItem(null);
    setKillAllModalOpen(false);
    setRenameModalItem(null);
    setExpireModalItem(null);
    setScheduledExpiries({});
  }, [orgId]);

  useEffect(() => {
    if (!orgId) {
      setOrgName('');
      setBusinessMode('hospitality_retail');
      setAppVariant('shared_device');
      return;
    }

    const propName = String(org?.name || org?.orgName || org?.label || '');
    const propMode = normalizeBusinessMode(org?.businessMode || org?.mode || org?.business_mode);
    const propVariant = normalizeAppVariant(org?.appVariant || org?.variant || org?.app_variant, propMode);

    if (propName) setOrgName(propName);
    if (propMode) setBusinessMode(propMode);
    if (propVariant) setAppVariant(propVariant);

    (async () => {
      try {
        const snap = await getDoc(doc(db, 'orgs', orgId));
        const d = snap.exists() ? snap.data() || {} : {};
        setOrgName(String(d.name || d.orgName || d.label || propName || ''));
        const nextMode = normalizeBusinessMode(d.businessMode || d.mode || d.business_mode || propMode);
        setBusinessMode(nextMode);
        setAppVariant(normalizeAppVariant(d.appVariant || d.variant || d.app_variant || propVariant, nextMode));
      } catch {
        setOrgName(propName || '');
        setBusinessMode(propMode || 'hospitality_retail');
        setAppVariant(propVariant || normalizeAppVariant('', propMode || 'hospitality_retail'));
      }
    })();
  }, [orgId, org]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!orgId) return undefined;

    const unsub = onSnapshot(
      query(orgCol(orgId, 'cashup_stores'), orderBy('name')),
      (snap) => {
        const rows = snap.docs
          .map((d) => ({ id: d.id, ...(d.data() || {}) }))
          .filter((s) => !(s.isArchived === true || s.archived === true));

        setStores(rows);
        setStoreId((prev) => {
          if (prev && rows.some((r) => r.id === prev)) return prev;
          return rows[0]?.id || '';
        });
      }
    );

    return () => unsub();
  }, [orgId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!orgId) return undefined;
    const unsub = onSnapshot(collection(db, 'orgs', orgId, 'device_aliases'), (snap) => {
      const next = {};
      snap.forEach((d) => {
        const data = d.data() || {};
        next[d.id] = {
          alias: String(data.alias || '').trim(),
          updatedAt: data.updatedAt || null,
        };
      });
      setAliases(next);
    });
    return () => unsub();
  }, [orgId]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!orgId) return undefined;
    const unsub = onSnapshot(collection(db, 'orgs', orgId, 'device_session_expiries'), (snap) => {
      const next = {};
      snap.forEach((d) => {
        next[d.id] = d.data() || {};
      });
      setScheduledExpiries(next);
    });
    return () => unsub();
  }, [orgId]);

  const orgInstallUrl = useMemo(() => {
    if (!orgId) return '';
    try {
      const url = new URL(base);
      const fieldMode = isFieldMode(businessMode);
      if (fieldMode) {
        url.pathname = `${url.pathname.replace(/\/$/, '')}/security`;
        url.searchParams.set('org', orgId);
        url.searchParams.set('mode', String(businessMode || 'field_services'));
      } else {
        url.searchParams.set('org', orgId);
        url.searchParams.set('firstStore', 'auto');
      }
      if (orgName) url.searchParams.set('orgName', orgName);
      return url.toString();
    } catch {
      return '';
    }
  }, [orgId, base, orgName, businessMode]);

  const perStoreUrl = useMemo(() => {
    if (!orgId || !storeId || !token) return '';
    try {
      const selected = stores.find((s) => s.id === storeId);
      const storeName = String(selected?.name || selected?.storeName || selected?.label || '');

      const url = new URL(base);
      if (isFieldMode(businessMode)) {
        url.pathname = `${url.pathname.replace(/\/$/, '')}/security`;
        url.searchParams.set('mode', normalizeBusinessMode(businessMode));
      }
      url.searchParams.set('org', orgId);
      url.searchParams.set('store', storeId);
      url.searchParams.set('token', token);
      if (expAtMs) url.searchParams.set('exp', String(expAtMs));
      if (orgName) url.searchParams.set('orgName', orgName);
      if (storeName) url.searchParams.set('storeName', storeName);

      return url.toString();
    } catch {
      return '';
    }
  }, [orgId, storeId, token, base, expAtMs, stores, orgName, businessMode]);

  useEffect(() => {
    if (!advanced) return undefined;
    const id = requestAnimationFrame(() => {
      if (orgInstallUrl && orgCanvasRef.current) {
        QRCode.toCanvas(orgCanvasRef.current, orgInstallUrl, { margin: 1, width: 220 }).catch(() => {});
      }
    });
    return () => cancelAnimationFrame(id);
  }, [orgInstallUrl, advanced]);

  useEffect(() => {
    if (perStoreUrl && perStoreCanvasRef.current) {
      QRCode.toCanvas(perStoreCanvasRef.current, perStoreUrl, { margin: 1, width: 220 }).catch(() => {});
    }
  }, [perStoreUrl]);

  const mergedDevices = useMemo(() => {
    return [...devices]
      .map((item) => ({
        ...normalizeDevice(item),
        alias: aliases?.[normalizeDevice(item).uid]?.alias || item.alias || '',
        scheduledExpiryAt: scheduledExpiries?.[normalizeDevice(item).uid]?.expiresAt || null,
        scheduledExpiryReason: scheduledExpiries?.[normalizeDevice(item).uid]?.reason || '',
      }))
      .filter((item) => item.uid)
      .sort((a, b) => {
        const linkedDiff = tsMs(b.linkedAt) - tsMs(a.linkedAt);
        if (linkedDiff !== 0) return linkedDiff;
        return tsMs(b.lastUsedAt || b.lastSeenAt) - tsMs(a.lastUsedAt || a.lastSeenAt);
      });
  }, [devices, aliases, scheduledExpiries]);

  const activeDevices = useMemo(
    () => mergedDevices.filter((item) => String(item.status || '').toLowerCase() !== 'killed'),
    [mergedDevices]
  );

  const loadDevices = async () => {
    if (!orgId) return;
    setLoadingDevices(true);
    setDeviceErr('');
    setDeviceMsg('');
    try {
      await ensureFreshAuth();

      // 1) Read the current org-level deviceBindings collection first.
      // This is the live source of truth for newly linked Neo devices.
      try {
        const directDevices = await loadDevicesDirect(orgId);
        if (directDevices.length > 0) {
          setDevices(directDevices);
          return;
        }
      } catch (directErr) {
        console.warn('[DownloadTab] direct deviceBindings read failed, trying callable/fallback', directErr);
      }

      // 2) Then try the callable in case rules block direct reads in some environments.
      let callableDevices = [];
      let callableError = null;
      try {
        const listOrgDeviceBindings = httpsCallable(functions, 'listOrgDeviceBindings');
        const res = await listOrgDeviceBindings({ orgId });
        callableDevices = Array.isArray(res?.data?.devices)
          ? res.data.devices
          : Array.isArray(res?.data)
          ? res.data
          : [];
      } catch (e) {
        callableError = e;
        console.warn('[DownloadTab] callable device list failed, trying Firestore fallback', e);
      }

      const normalizedCallableDevices = callableDevices.map((item) => normalizeDevice(item)).filter((item) => item.uid);

      if (normalizedCallableDevices.length > 0) {
        setDevices(normalizedCallableDevices);
        return;
      }

      // 3) Finally try broader fallback collection scans for older structures.
      const fallbackDevices = await loadDevicesFromFirestoreFallback(orgId);
      if (fallbackDevices.length > 0) {
        setDevices(fallbackDevices);
        if (callableError) {
          setDeviceMsg('Loaded devices from Firestore fallback.');
        }
        return;
      }

      setDevices([]);
      if (callableError) {
        setDeviceErr(friendlyError(callableError) || 'Failed to load connected devices.');
      }
    } catch (e) {
      console.error('Failed to load devices', e);
      setDevices([]);
      setDeviceErr(friendlyError(e) || 'Failed to load connected devices.');
    } finally {
      setLoadingDevices(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (orgId) loadDevices();
  }, [orgId]);

  const genPin = async () => {
    if (!storeId || !orgId) {
      showNotice('Select organization and store', 'Please select an organization and store first.');
      return;
    }

    setGenLoading(true);
    setPin('');
    setToken('');
    setExpAtMs(null);

    try {
      const ttlSeconds = Math.max(1, Number(ttlMin)) * 60;
      await ensureFreshAuth();
      const createPin = httpsCallable(functions, 'createStoreInstallPin');
      const res = await createPin({
        orgId,
        storeId,
        ttlSeconds,
        businessMode,
        appVariant,
      });
      const { pin: nextPin, token: nextToken, expiresAt } = res.data || {};
      setPin(nextPin || '');
      setToken(nextToken || '');
      setExpAtMs(Number(expiresAt) || null);
    } catch (e) {
      console.error(e);
      showNotice('Unable to generate install PIN', friendlyError(e) || 'Failed to generate PIN');
    } finally {
      setGenLoading(false);
    }
  };


  const debugTokenClaims = async () => {
    try {
      const user = await ensureFreshAuth();
      const result = await user.getIdTokenResult(true);
      const claims = result?.claims || {};
      console.log('[DownloadTab] token claims', claims);
      const summary = [
        `uid: ${user?.uid || '—'}`,
        `email: ${user?.email || '—'}`,
        `superAdmin: ${String(claims?.superAdmin ?? 'missing')}`,
        `superadmin: ${String(claims?.superadmin ?? 'missing')}`,
        `role: ${String(claims?.role ?? 'missing')}`,
        `orgId: ${String(claims?.orgId ?? 'missing')}`,
      ].join('\n');
      showNotice('Current token claims', summary);
    } catch (e) {
      console.error(e);
      showNotice('Token debug failed', friendlyError(e) || 'Could not read token claims.');
    }
  };

  const runKill = async (item, { reason = '', impact = '', mode = 'kill' } = {}) => {
    if (!orgId || !item?.storeId || !item?.uid) return;
    setBusyKill(true);
    setDeviceMsg('');
    setDeviceErr('');
    try {
      const user = await ensureFreshAuth();
      const killOrgDeviceBindings = httpsCallable(functions, 'killOrgDeviceBindings');
      const res = await killOrgDeviceBindings({ orgId, storeId: item.storeId, deviceUid: item.uid, scope: 'single' });

      // Mirror the kill state directly into Firestore so the Neo app heartbeat
      // sees the kill even if this dashboard is pointed at a different functions source.
      await mirrorKilledStateToFirestore({
        orgId,
        item,
        reason: reason || (mode === 'expire' ? 'Session expired by super admin' : 'Killed by super admin'),
        impact: impact || '',
        actorUid: user?.uid || '',
        actorEmail: user?.email || '',
      });

      setDevices((prev) =>
        prev.map((row) =>
          String(normalizeDevice(row).uid) === String(item.uid)
            ? { ...row, status: 'killed', shouldUnlink: true, killedAt: new Date().toISOString(), lastReason: reason || (mode === 'expire' ? 'Session expired by super admin' : 'Killed by super admin') }
            : row
        )
      );
      const payload = {
        type: mode === 'expire' ? 'expire_session' : 'kill_device',
        orgId,
        orgName: orgName || org?.name || '',
        storeId: item.storeId || '',
        storeName: item.storeName || '',
        deviceUid: item.uid,
        deviceName: item.alias || item.deviceLabel || item.userAgent || item.storeName || item.storeId || '',
        reason: reason || (mode === 'expire' ? 'Session expired by super admin' : 'Killed by super admin'),
        impact: impact || '',
        statusBefore: item.status || 'linked',
        actorUid: user?.uid || '',
        actorEmail: user?.email || '',
      };
      const message = mode === 'expire' ? 'Session expired for device.' : `Kill switch sent to ${res?.data?.updated || 1} device.`;
      setDeviceMsg(message);
      await Promise.all([
        writeActionArchive({ orgId, payload }),
        writeKilledDeviceArchive({ orgId, item, payload }),
      ]);
      try {
        await setDoc(
          doc(db, 'orgs', orgId, 'device_aliases', item.uid),
          { lastReason: payload.reason, lastKilledAt: serverTimestamp() },
          { merge: true }
        );
      } catch {
        // noop
      }
      await loadDevices();
    } catch (e) {
      console.error(e);
      setDeviceErr(friendlyError(e) || 'Failed to kill device.');
    } finally {
      setBusyKill(false);
    }
  };

  const runKillAll = async ({ reason = '', impact = '' } = {}) => {
    if (!orgId) return;
    setBusyKill(true);
    setDeviceMsg('');
    setDeviceErr('');
    try {
      const user = await ensureFreshAuth();
      const killOrgDeviceBindings = httpsCallable(functions, 'killOrgDeviceBindings');
      const res = await killOrgDeviceBindings({ orgId, scope: 'org' });

      await mirrorKillAllStateToFirestore({
        orgId,
        items: activeDevices,
        reason: reason || 'Killed all linked devices by super admin',
        impact: impact || '',
        actorUid: user?.uid || '',
        actorEmail: user?.email || '',
      });

      setDevices((prev) =>
        prev.map((row) => ({
          ...row,
          status: 'killed',
          shouldUnlink: true,
          killedAt: new Date().toISOString(),
          lastReason: reason || 'Killed all linked devices by super admin',
        }))
      );
      const payload = {
        type: 'kill_all_devices',
        orgId,
        orgName: orgName || org?.name || '',
        storeId: '',
        storeName: 'All stores',
        deviceUid: '',
        deviceName: 'All linked devices',
        reason: reason || 'Killed all linked devices by super admin',
        impact: impact || '',
        statusBefore: 'linked',
        actorUid: user?.uid || '',
        actorEmail: user?.email || '',
      };
      setDeviceMsg(`Kill switch sent to ${res?.data?.updated || 0} device(s).`);
      await writeActionArchive({ orgId, payload });
      await loadDevices();
    } catch (e) {
      console.error(e);
      setDeviceErr(friendlyError(e) || 'Failed to kill all devices.');
    } finally {
      setBusyKill(false);
    }
  };

  const saveAlias = async (item, alias) => {
    if (!orgId || !item?.uid || !alias) return;
    setBusyRename(true);
    setDeviceErr('');
    setDeviceMsg('');
    try {
      const user = await ensureFreshAuth();
      await setDoc(
        doc(db, 'orgs', orgId, 'device_aliases', item.uid),
        {
          alias,
          deviceUid: item.uid,
          storeId: item.storeId || '',
          storeName: item.storeName || '',
          updatedAt: serverTimestamp(),
          updatedBy: user?.email || user?.uid || 'unknown',
        },
        { merge: true }
      );
      try {
        const key = `deviceAlias:${orgId}:${item.uid}`;
        window.localStorage.setItem(key, alias);
      } catch {
        // noop
      }
      await writeActionArchive({
        orgId,
        payload: {
          type: 'rename_device',
          orgId,
          orgName: orgName || org?.name || '',
          storeId: item.storeId || '',
          storeName: item.storeName || '',
          deviceUid: item.uid,
          deviceName: alias,
          previousName: item.alias || item.deviceLabel || item.userAgent || '',
          reason: 'Device renamed in super admin',
          actorUid: user?.uid || '',
          actorEmail: user?.email || '',
        },
      });
      setDeviceMsg('Device name saved.');
      setRenameModalItem(null);
    } catch (e) {
      console.error(e);
      try {
        const key = `deviceAlias:${orgId}:${item.uid}`;
        window.localStorage.setItem(key, alias);
        setAliases((prev) => ({ ...prev, [item.uid]: { ...(prev[item.uid] || {}), alias } }));
        setDeviceMsg('Device name saved locally in this browser.');
        setRenameModalItem(null);
      } catch {
        setDeviceErr(friendlyError(e) || 'Failed to rename device.');
      }
    } finally {
      setBusyRename(false);
    }
  };

  const processScheduledExpiry = async (item, scheduleMeta = null) => {
    if (!item?.uid) return;
    await runKill(item, {
      reason: scheduleMeta?.reason || item.scheduledExpiryReason || 'Scheduled session expiry reached',
      mode: 'expire',
    });
    try {
      await deleteDoc(doc(db, 'orgs', orgId, 'device_session_expiries', item.uid));
    } catch (e) {
      console.warn('[DownloadTab] failed to clear expiry schedule', e);
    }
  };

  const scheduleExpiry = async (item, { hours, reason, expiresAtMs }) => {
    if (!orgId || !item?.uid || !expiresAtMs) return;
    setBusyKill(true);
    setDeviceErr('');
    setDeviceMsg('');
    try {
      const user = await ensureFreshAuth();
      await setDoc(
        doc(db, 'orgs', orgId, 'device_session_expiries', item.uid),
        {
          orgId,
          orgName: orgName || org?.name || '',
          storeId: item.storeId || '',
          storeName: item.storeName || '',
          deviceUid: item.uid,
          deviceName: item.alias || item.deviceLabel || item.userAgent || item.storeName || item.storeId || '',
          hours: Number(hours || 0),
          reason: reason || 'Session expiry scheduled by super admin',
          expiresAt: new Date(expiresAtMs),
          status: 'scheduled',
          actorUid: user?.uid || '',
          actorEmail: user?.email || '',
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );
      await writeActionArchive({
        orgId,
        payload: {
          type: 'schedule_session_expiry',
          orgId,
          orgName: orgName || org?.name || '',
          storeId: item.storeId || '',
          storeName: item.storeName || '',
          deviceUid: item.uid,
          deviceName: item.alias || item.deviceLabel || item.userAgent || item.storeName || item.storeId || '',
          reason: reason || 'Session expiry scheduled by super admin',
          actorUid: user?.uid || '',
          actorEmail: user?.email || '',
          expiresAt: new Date(expiresAtMs),
          hours: Number(hours || 0),
        },
      });
      setDeviceMsg(`Session expiry scheduled for ${new Date(expiresAtMs).toLocaleString()}.`);
      setExpireModalItem(null);
    } catch (e) {
      console.error(e);
      setDeviceErr(friendlyError(e) || 'Failed to schedule session expiry.');
    } finally {
      setBusyKill(false);
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!orgId) return undefined;
    const id = window.setInterval(() => {
      const now = Date.now();
      activeDevices.forEach((item) => {
        const expMs = tsMs(item.scheduledExpiryAt);
        const scheduleMeta = scheduledExpiries?.[item.uid] || null;
        if (expMs && expMs <= now && scheduleMeta?.status !== 'processing') {
          setScheduledExpiries((prev) => ({
            ...prev,
            [item.uid]: { ...(prev[item.uid] || {}), status: 'processing' },
          }));
          processScheduledExpiry(item, scheduleMeta);
        }
      });
    }, 30000);
    return () => window.clearInterval(id);
  }, [orgId, activeDevices, scheduledExpiries]);

  useEffect(() => {
    if (!orgId || Object.keys(aliases || {}).length > 0) return;
    try {
      const next = {};
      for (let i = 0; i < window.localStorage.length; i += 1) {
        const key = window.localStorage.key(i);
        if (!key || !key.startsWith(`deviceAlias:${orgId}:`)) continue;
        const uid = key.split(':').pop();
        next[uid] = { alias: window.localStorage.getItem(key) || '' };
      }
      if (Object.keys(next).length) setAliases((prev) => ({ ...next, ...prev }));
    } catch {
      // noop
    }
  }, [orgId, aliases]);

  const queryOrgId = getQueryOrgId();
  const hasOrgContextMismatch = Boolean(queryOrgId && orgIdProp && String(queryOrgId).trim() !== String(orgIdProp).trim());

  return (
    <div className="super-download-page" style={{ color: '#e5e7eb' }}>
      <NoticeModal
        open={notice.open}
        title={notice.title}
        message={notice.message}
        onClose={() => setNotice({ open: false, title: '', message: '' })}
      />

      <KillConfirmModal
        open={!!killModalItem}
        item={killModalItem}
        busy={busyKill}
        onClose={() => (!busyKill ? setKillModalItem(null) : null)}
        onConfirm={async ({ reason, impact }) => {
          await runKill(killModalItem, { reason, impact, mode: 'kill' });
          setKillModalItem(null);
        }}
      />

      <NoticeModal
        open={notice.open}
        title={notice.title}
        message={notice.message}
        onClose={() => setNotice({ open: false, title: '', message: '' })}
      />

      <KillConfirmModal
        open={!!killAllModalOpen}
        item={{ isBulk: true, uid: '__all__', orgName: orgName || org?.name || 'Selected organization', count: devices.length }}
        busy={busyKill}
        onClose={() => (!busyKill ? setKillAllModalOpen(false) : null)}
        onConfirm={async ({ reason, impact }) => {
          await runKillAll({ reason, impact });
          setKillAllModalOpen(false);
        }}
      />

      <ExpireSessionModal
        open={!!expireModalItem}
        item={expireModalItem}
        busy={busyKill}
        onClose={() => (!busyKill ? setExpireModalItem(null) : null)}
        onSchedule={async (payload) => {
          await scheduleExpiry(expireModalItem, payload);
        }}
      />

      <RenameDeviceModal
        open={!!renameModalItem}
        item={renameModalItem}
        busy={busyRename}
        onClose={() => (!busyRename ? setRenameModalItem(null) : null)}
        onSave={async (alias) => {
          await saveAlias(renameModalItem, alias);
        }}
      />

      {hasOrgContextMismatch ? (
        <div style={{ marginBottom: 14, padding: '12px 14px', borderRadius: 12, background: 'rgba(245,158,11,0.10)', border: '1px solid rgba(245,158,11,0.24)', color: '#fde68a' }}>
          The page detected an old organization id in the URL. Download tools now follow the selected organization first, then sync the URL.
        </div>
      ) : null}

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <div>
          <div style={{ fontSize: 16, fontWeight: 700 }}>Download</div>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button
            onClick={debugTokenClaims}
            style={{
              padding: '8px 12px',
              borderRadius: 8,
              border: '1px solid #334155',
              background: '#0f172a',
              color: '#93c5fd',
            }}
          >
            Debug token
          </button>
          <button
            onClick={() => setAdvanced((v) => !v)}
            style={{
              padding: '8px 12px',
              borderRadius: 8,
              border: '1px solid #334155',
              background: advanced ? '#1e293b' : '#334155',
              color: '#fff',
            }}
          >
            {advanced ? 'Hide advanced' : 'Advanced'}
          </button>
        </div>
      </div>

      <div style={{ background: '#0b1220', border: '1px solid #1f2937', borderRadius: 16, padding: 16, marginBottom: 22 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'flex-start', flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Mode & install flow</div>
            <div style={{ color: '#94a3b8', fontSize: 13, marginTop: 6 }}>Business mode controls how the Neo app opens from Super Admin.</div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ padding: '6px 10px', borderRadius: 999, background: '#082f49', color: '#7dd3fc', border: '1px solid #0ea5e9' }}>{businessMode.replace(/_/g, ' ')}</span>
            <span style={{ padding: '6px 10px', borderRadius: 999, background: '#1f2937', color: '#e5e7eb', border: '1px solid #334155' }}>{appVariant.replace(/_/g, ' ')}</span>
          </div>
        </div>

        <div style={{ fontWeight: 700, fontSize: 16, marginTop: 20, marginBottom: 16 }}>Per-store link with one-time PIN</div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
          <div>
            <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 6 }}>Store</div>
            <select value={storeId} onChange={(e) => setStoreId(e.target.value)} style={{ width: '100%', padding: '10px 12px', background: '#020617', color: '#fff', border: '1px solid #334155', borderRadius: 8 }}>
              {stores.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.name || s.storeName || s.label || s.id}
                </option>
              ))}
            </select>
          </div>
          <div>
            <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 6 }}>Expires in (minutes)</div>
            <input type="number" min={1} value={ttlMin} onChange={(e) => setTtlMin(e.target.value)} style={{ width: '100%', padding: '10px 12px', background: '#020617', color: '#fff', border: '1px solid #334155', borderRadius: 8 }} />
          </div>
          <div style={{ display: 'flex', alignItems: 'flex-end' }}>
            <button onClick={genPin} disabled={genLoading || !storeId || !orgId} style={{ width: '100%', padding: '10px 12px', borderRadius: 8, border: '1px solid #0891b2', background: '#0891b2', color: '#fff', opacity: (genLoading || !storeId || !orgId) ? 0.6 : 1 }}>
              {genLoading ? 'Generating…' : (isFieldMode(businessMode) ? 'Generate security PIN' : 'Generate PIN')}
            </button>
          </div>
        </div>

        {isFieldMode(businessMode) ? (
          <div style={{ color: '#93c5fd', fontSize: 12, marginTop: 6 }}>This security PIN opens the device into the Neo Security flow after linking. Same install pattern, different post-link experience.</div>
        ) : null}

        {perStoreUrl ? (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr auto', gap: 12, marginTop: 16 }}>
            <div>
              <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 6 }}>{isFieldMode(businessMode) ? 'Security device link' : 'Per-store link'}</div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input value={perStoreUrl} readOnly style={{ flex: 1, padding: '10px 12px', background: '#020617', color: '#fff', border: '1px solid #334155', borderRadius: 8 }} />
                <button onClick={() => copy(perStoreUrl, 'storeLink')} style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #334155', background: '#334155', color: '#fff' }}>{copiedKey === 'storeLink' ? 'Copied' : 'Copy'}</button>
              </div>
            </div>
            <div>
              <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 6 }}>{isFieldMode(businessMode) ? 'Security PIN (share separately)' : 'One-time PIN (share separately)'}</div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div style={{ padding: '10px 12px', minWidth: 140, textAlign: 'center', background: '#020617', border: '1px solid #334155', borderRadius: 8, fontSize: 20, fontWeight: 700 }}>{pin || '—'}</div>
                <button onClick={() => copy(pin, 'pin')} style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #334155', background: '#334155', color: '#fff' }}>{copiedKey === 'pin' ? 'Copied' : 'Copy'}</button>
                <div style={{ color: '#cbd5e1', fontSize: 13 }}>Expires in {expired ? '00:00' : label}</div>
              </div>
              <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 8 }}>{isFieldMode(businessMode) ? 'PIN is valid once until it expires. Use this on the link-device step, then the phone opens Neo Security.' : 'PIN is valid once until it expires. Not included in the link/QR.'}</div>
            </div>
            <div style={{ display: 'flex', gap: 16, alignItems: 'center' }}>
              <canvas ref={perStoreCanvasRef} style={{ background: '#fff', padding: 8, borderRadius: 8 }} />
              <div style={{ color: '#cbd5e1', fontSize: 13 }}>{isFieldMode(businessMode) ? 'Scan to open the Neo Security install flow for this store. Staff must enter the security PIN on link-device.' : 'Scan to open the app for this store. Staff must enter the one-time PIN.'}</div>
            </div>
          </div>
        ) : null}
      </div>

      <div style={{ background: '#0f172a', border: '1px solid #1f2937', borderRadius: 16, overflow: 'hidden' }}>
        <div style={{ padding: '16px 16px 10px', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 16 }}>Connected Neo devices</div>
            <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 6 }}>Newest links sit on top. Rename devices, track last used, and manage kill or expire actions.</div>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={loadDevices} style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #334155', background: '#0f172a', color: '#fff' }}>Refresh</button>
            <button
              onClick={() => {
                if (!orgId || busyKill) return;
                setDeviceErr('');
                setDeviceMsg('');
                setKillAllModalOpen(true);
              }}
              disabled={busyKill}
              style={{ padding: '10px 12px', borderRadius: 8, border: '1px solid #7f1d1d', background: '#b91c1c', color: '#fff', opacity: busyKill ? 0.6 : 1 }}
            >
              Kill all devices
            </button>
          </div>
        </div>

        {deviceMsg ? <div style={{ margin: '0 16px 12px', padding: '12px 14px', borderRadius: 12, background: 'rgba(8,145,178,0.12)', border: '1px solid rgba(6,182,212,0.2)', color: '#ecfeff' }}>{deviceMsg}</div> : null}
        {deviceErr ? <div style={{ margin: '0 16px 12px', padding: '12px 14px', borderRadius: 12, background: 'rgba(127,29,29,0.18)', border: '1px solid rgba(248,113,113,0.2)', color: '#fee2e2' }}>{deviceErr}</div> : null}

        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(300px,1.35fr) minmax(160px,0.8fr) minmax(140px,0.8fr) minmax(140px,0.9fr) auto', gap: 12, padding: '0 16px 10px', color: '#93c5fd', fontSize: 12, textTransform: 'uppercase' }}>
          <div>Device</div>
          <div>Status</div>
          <div>Linked</div>
          <div>Last used</div>
          <div style={{ textAlign: 'right' }}>Action</div>
        </div>

        {loadingDevices ? (
          <div style={{ padding: 18, color: '#94a3b8' }}>Loading devices…</div>
        ) : activeDevices.length === 0 ? (
          <div style={{ padding: 18, color: '#94a3b8' }}>No linked devices found for this organization yet.</div>
        ) : (
          activeDevices.map((item) => (
            <DeviceRow
              key={item.uid}
              item={item}
              busy={busyKill || busyRename}
              onRename={setRenameModalItem}
              onExpire={setExpireModalItem}
              onKill={setKillModalItem}
            />
          ))
        )}
      </div>
    </div>
  );
}
